import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../config';

// Generic link-preview card (any url) — calls our server-side /api/unfurl
// (browser CORS makes client-side OG-scraping impossible). Renders the same
// og:image/title/description a messenger would unfurl, incl. traderbro's
// generated cards. Silent (renders nothing) if there's no usable metadata.
const _cache = new Map(); // url -> data | 'fail', shared across mounts this session

export default function LinkPreview({ url }) {
  const [data, setData] = useState(() => {
    const c = _cache.get(url);
    return c && c !== 'fail' ? c : null;
  });
  const [failed, setFailed] = useState(() => _cache.get(url) === 'fail');

  useEffect(() => {
    if (data || failed || _cache.has(url)) {
      if (_cache.get(url) === 'fail') setFailed(true);
      else if (_cache.has(url)) setData(_cache.get(url));
      return;
    }
    let alive = true;
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/api/unfurl/?url=${encodeURIComponent(url)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => {
        if (!alive) return;
        if (d && (d.title || d.image)) { _cache.set(url, d); setData(d); }
        else { _cache.set(url, 'fail'); setFailed(true); }
      })
      .catch(() => { if (alive) { _cache.set(url, 'fail'); setFailed(true); } });
    return () => { alive = false; };
  }, [url, data, failed]);

  if (failed || !data) return null;

  let host = data.siteName;
  if (!host) { try { host = new URL(url).hostname; } catch { host = ''; } }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block mt-2 max-w-md rounded-lg overflow-hidden border border-gray-700/60 bg-[#0f1018] hover:border-blue-500/60 transition no-underline"
    >
      {data.image && (
        <img
          src={data.image}
          alt=""
          loading="lazy"
          className="w-full max-h-48 object-cover bg-[#0b0c14]"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      )}
      <div className="p-2.5">
        {host && <div className="text-[10px] uppercase tracking-wide text-gray-500">{host}</div>}
        {data.title && (
          <div className="text-sm font-medium text-gray-100 mt-0.5 line-clamp-2">{data.title}</div>
        )}
        {data.description && (
          <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{data.description}</div>
        )}
      </div>
    </a>
  );
}
